/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Dropdown, Menu } from 'antd';
import Upload, { UploadProps } from 'antd/lib/upload';
import { uniqBy } from 'lodash';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import IconButton from '~components/_common/IconButton/IconButton';
import MobileViewContext from '~src/contexts/MobileViewContext';
import { useModalActions } from '~src/contexts/ModalsContext';
import UploadContext from '~src/contexts/UploadContext';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useIsPath } from '~src/hooks/useIsPath';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { useProjectId } from '~src/hooks/useProjectId';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { RIGHTS_LEVEL } from '~src/types/enums';
import { FileX } from '~src/types/upload';
import { appLog } from '~src/utils/log';

interface FooterProps {
  setShowSearch: Dispatch<SetStateAction<boolean>>;
}

export const Footer: React.FC<FooterProps> = ({ setShowSearch }) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const history = useHistory();
  const projectId = useProjectId();
  const currentFolder = useCurrentFolder();

  const isOnRoot = useIsPath('/archive', { endsWith: true });
  const isSuperuser = useIsSuperuser();
  const createFolderModal = useModalActions('createFolderModal');

  const { setUploadContext } = useContext(UploadContext);

  const permissionLevel = currentFolder?.permission || 0;

  // render upload buttons only on folders with appropiate permission
  const uploadFileAllowed = permissionLevel >= RIGHTS_LEVEL.UPLOAD;

  const handleUploadChange: UploadProps['onChange'] = (info) => {
    setUploadContext((prevFiles) => {
      const file = info.fileList.find((v) => v.uid === info.file.uid)
        ?.originFileObj as FileX | undefined;
      if (!file) return prevFiles;

      return uniqBy(
        [file, ...prevFiles],
        (v) =>
          `${v.name}@${
            v.webkitRelativePath === v.name ? '' : v.webkitRelativePath
          }`
      );
    });
  };

  let showCreateFolderBtn = false;
  if (!isOnRoot) {
    showCreateFolderBtn = permissionLevel >= RIGHTS_LEVEL.WRITE;
  } else if (isSuperuser) {
    showCreateFolderBtn = true;
  }

  const plusMenuOverlay = (
    <Menu>
      <Menu.Item>
        <Upload
          multiple
          beforeUpload={() => false}
          onChange={handleUploadChange}
          disabled={!uploadFileAllowed}>
          <div css={styles.item}>
            <IconButton
              iconStyle="far"
              icon="file-upload"
              customStyle={styles.icons}
              tooltip={intl.formatMessage({ id: 'upload-files' })}
              disabled={!uploadFileAllowed}
            />
            <span css={styles.text}>
              {intl.formatMessage({ id: 'upload-file' })}
            </span>
          </div>
        </Upload>
      </Menu.Item>
      <Menu.Item>
        <div
          css={styles.item}
          onClick={() =>
            showCreateFolderBtn &&
            createFolderModal.open({ parent: currentFolder })
          }>
          <IconButton
            iconStyle="far"
            icon="folder-plus"
            customStyle={styles.icons}
            disabled={!showCreateFolderBtn}
            tooltip={intl.formatMessage({ id: 'create-folder' })}
          />
          <span css={styles.text}>
            {intl.formatMessage({ id: 'create-folder' })}
          </span>
        </div>
      </Menu.Item>
    </Menu>
  );

  return (
    <div css={styles.container}>
      <IconButton
        click={() => history.push(`/projects/${projectId}`)}
        icon={['far', 'home']}
        backgroundOff
        customStyle={styles.icons}
      />
      <Dropdown
        trigger={['click']}
        placement="topCenter"
        arrow
        overlay={plusMenuOverlay}>
        <a
          className="ant-dropdown-link"
          onClick={(e) => e.preventDefault()}
          css={styles.plusAnchor}>
          <IconButton
            click={() => appLog('desktop')}
            icon={['far', 'plus-circle']}
            backgroundOff
            customStyle={styles.plusIcon}
          />
        </a>
      </Dropdown>
      <IconButton
        click={() => setShowSearch((prevState) => !prevState)}
        icon={['fas', 'search']}
        backgroundOff
        customStyle={styles.icons}
      />
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    position: 'fixed',
    left: 0,
    bottom: 0,
    width: '100%',
    height: 60,
    backgroundColor: theme.colors.white,
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'center',
    boxShadow:
      'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
  },
  plusIcon: {
    fontSize: 25,
    color: theme.colors.primary,
    paddingBottom: 5,
  },
  icons: {
    fontSize: 20,
  },
  item: {
    display: 'flex',
    flexDirection: 'row',
    padding: '2px 15px',
    cursor: 'pointer',

    '&:active': {
      backgroundColor: theme.colors.primaryLight,
    },
    '&:hover': {
      backgroundColor:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? `${theme.colors.secondaryLight} !important`
          : '',
    },

    '&:not(.ant-dropdown-menu-item-disabled)': {
      color: theme.colors.text.primary,
    },
  },
  plusAnchor: {
    color: theme.colors.darkGray,
    paddingTop: 5,
    textDecoration: 'none',
    '&:hover': {
      color: theme.colors.darkGray,
    },
  },
  text: {
    fontSize: '16px',
    padding: '3px 6px',
    userSelect: 'none',
  },
});
